import {_, React, Suspense, useAsObservableSource, useEffect, useLayoutEffect, useLocalStore, useMemo, observer, Fragment} from '@api'
import {Portal, Spinner} from '@blueprintjs/core'
import {LoadingIndicator, SidebarAlign, CatalogItemSidebarDetailsContext, SidebarDetailsProps, CatalogItemSidebarDetailsStore, useCatalogItemSidebarDetailsStore, useVipLocalStore, useStores, ErrorBoundary, DEBUG_ITEM_DETAILS} from '@modules'
import {LeftSidebarHeader} from '@modules/catalog/sidebar/LeftSidebar'
import {onSiteError, site, useCatalog} from '@stores'
import {DetailsViewSectionConfig} from '@stores/config'
import {CollapsibleStateful, PrimaryButton, SecondaryButton, SidebarCarousel, Slider, SyncProgressBar} from 'centric-ui-components'
import classNames from 'classnames'
import * as css from './CatalogItemSidebarDetails.css'
import {DetailsSecondariesStore} from './Modal/DetailsSecondaryStore'
import {CatalogItemModelContents, CatalogFieldRenderer, CatalogSecondariesDetails} from './'
import {TableRegionView} from './SecondariesAdoptionRegion'
import {action, observable} from 'mobx'

export const CatalogItemSidebarDetailsLoader = observer<SidebarDetailsProps>(function CatalogItemSidebarDetailsLoader(props) {
  const {sidebar, storeId, description} = useStores(), {align} = props

  DEBUG_ITEM_DETAILS && console.log({storeId, description, details: sidebar[align].details, where: props.where})

  if (_.isEmpty(props.where)) return null
  if (!sidebar[align].details) return null

  return <CatalogItemSidebarDetailsLoaderInternal {...props} />
})

  /**
 * Details sidebar for a specific item
 * This might be a library item selected from the global library, it might be the right sidebar (product details), it could be specific details of a grouped specification,
 * or a specific subfield from a catalog item like when viewing colorways from a right sidebar item
 *
 * Because of this we may have different criteria we have to search from to find the underlying item.  For example we may only have someCatalogItem.style.colorways[0].exId, but when listing the entire colorway catalog, we'd have _ids,
 * etc...
 * @param props
 * @constructor
 */
export const CatalogItemSidebarDetailsLoaderInternal = observer<SidebarDetailsProps>(function CatalogItemSidebarDetailsLoaderInternal(props) {
  const stores = useStores(),
    {sidebar: sidebarStore, catalog: {styleCssVariables}} = stores,
    {details: detailsStore} = sidebarStore[props.align],
    store = detailsStore

  if (!store) {
    debugger
    return null
  }

  const {error, isFirstRender, isSyncing, isNavigating, isInitialLoad, isApplyingMutations, data, index, where, props: {isCreate}, sidebar, loading} = store,
    id = where?.[0]?.value ?? store.props.idWithPath,
    differentObject = data?.id !== id,
    anchorEl = React.useRef<HTMLDivElement>(null)

  // useEffect(() => {
  //   console.log(`mount details ${id}`)
  //   return () => console.log(`unmount details ${id}`)
  // }, [])

  useEffect(() => !store?.isForNav && store.registerEffects(), [store])
  useEffect(() => {
    store.anchorEl = anchorEl.current
  }, [store, anchorEl.current])

  // ASHISH RATTAN: Intentionally commented this because Save button was showing disabled with infinite loader when Sync button is clicked
  // useLayoutEffect(() => {
  //   store.isApplyingMutations = !isInitialLoad
  // }, [catalog.es.refetchId])

  // useLayoutEffect(() => {
  //   if (store.sidebar) {
  //     store.isInitialLoad = true
  //   }
  // }, [store.sidebar, where, props.library, store.loading])

  /*const {} = useAsync(
    async () => {
      console.log(`(Re)loading item detail data`, itemDetailsStore)
      if (hasError) return null
      else if (itemDetailsStore && catalog.hasLoadedConfig) {
        // console.log(`Running query for `, toJS({store: stores._id, where, hasError, refetchId: catalog.es.refetchId, library: props.library?.id}, {recurseEverything: true}))
        if (isCreate) {
          itemDetailsStore.replaceData(_data)
          return null
        } else {
          if (store.isInitialLoad && !_.isEmpty(navStore?.data)) {
            store.isInitialLoad = false
            itemDetailsStore.replaceData(navStore.data)
          } else {
            return await store.loadData()
          }
        }
      }
      //return hasError ? null : isCreate ? (store.data = _data) && (await createItem.loadData()) : await store.loadData()
    }, [itemDetailsStore, catalog.es.refetchId, props.library, hasError, _.last(where)?.value])*/

  const showLoader = !detailsStore || (!isCreate && !isSyncing && isInitialLoad && (loading || store.loading) && differentObject) // Only show loader on initial load or you'll ruin nav between items in the detail hierarchy

  // console.log(toJS({showLoader, navItems, selectedNavStore, path}, {recurseEverything: true}))
  // Don't popup loading errors
  // console.log({error, loading})
  // useEffect(() => {
  //   (error || isError) && onSiteError(error || uploadError)
  // }, [error, isError, uploadError])

  // useEffect(() => {
  //   console.log(`Mount CatalogItemSidebarDetailsLoader`)
  //   return () => console.log(`Unmount CatalogItemSidebarDetailsLoader`)
  // }, [])

  // console.log({loading, isInitialLoad, showLoader, props})

  return (
    <CatalogItemSidebarDetailsContext.Provider value={store}>
      <ErrorBoundary>
      <div
        style={_.merge({}, styleCssVariables, props.style)}
        data-first-render={isFirstRender}
        onKeyDown={sidebar.onKeyDown}
        className={classNames(props.className, css.detailsLoader)}
          // Annoying behavior on refetch
          // 'animate__animated',
          // sidebar?.type == 'modal' && [
          //   'animate__fadeIn'
          // ])}
        ref={anchorEl}
        data-loading={showLoader}
        data-type='CatalogItemSidebarDetailsLoader'>
        <Suspense fallback={<LoadingIndicator/>}>
          {showLoader ?
           <Fragment>
             {/*detailsStore?.sidebar?.flexLocation !== 'catalogItemDetails' && */ <LoadingIndicator className={css.loader}/>}
           </Fragment>
          : data || isCreate || loading || store.itemDetailsStore
            ? <CatalogItemSidebarDetails {...props} />
            : <div className={css.notFound} data-loading={true} data-error={error != null}>
              {loading || !error ? null : !isInitialLoad && DEBUG ? <Fragment>
                       <span>ES _doc not found:</span>
                       <pre className={css.repro}>
                  GET {index}/_search
                         {JSON.stringify(store.searchPayload, null, 2)}
                </pre>
                     </Fragment>
                     : <Fragment>
                 <span className={css.error}>Error loading item</span>
               </Fragment>}
            </div>}
        </Suspense>
      </div>
      </ErrorBoundary>
    </CatalogItemSidebarDetailsContext.Provider>
  )
})


export const CatalogItemSidebarDetails = observer<SidebarDetailsProps>(function CatalogItemSidebarDetails(props) {
  const details = useCatalogItemSidebarDetailsStore(),
    secondariesStore = useVipLocalStore((ws) => new DetailsSecondariesStore(ws, details)),
    {
      canAddImage, drag, detailDef, data, isApplyingMutations, catalog,
      isDirty, mutationSaveCatalogItemDetails, cancelMutations, isUploading, id, flexLayout: {isMaximized},
      localImages, sidebar, sidebar: {selectedPathItem, showImageFullscreen, type, associatedWorkspace}, isOffline, isValidForUpload, secondarySection,
    } = details,
    {align, context} = props,
    disableUpload = align === 'left' || !(context === 'catalog') || canAddImage === false,
    sections = _.concat(_.filter(detailDef?.sidebar?.sections || [], (s) => s.type !== 'secondaries'), secondarySection || []),
    saveButton = useMemo(() => <PrimaryButton className={css.primaryButton}
                                              disabled={isApplyingMutations || (!isValidForUpload && !isDirty)}
                                              onClick={mutationSaveCatalogItemDetails}>
      Save
      {isApplyingMutations && <Spinner size={14} className={css.applyingMutationsSpinner}/>}
    </PrimaryButton>, [isApplyingMutations, isValidForUpload, isDirty, isOffline])

  useEffect(() => {
    secondariesStore._runSecondariesQuery()
  }, [details?.id, details?.data?.secondaries])

  // useEffect(() => {
  //   console.log(`Mount CatalogItemSidebarDetails`)
  //   return () => {
  //     console.log(`Unmount CatalogItemSidebarDetails`, {props})
  //   }
  // }, [])

  return <Fragment>
    {detailDef?.syncId && <SyncProgressBar id={id} className={css.syncProgress}/>}
    <div className={css.root}
         key={details.id}
         data-type='CatalogItemSidebarDetails'
         data-detail-type={type}
         data-align={details.align}
         data-dirty={isDirty === true}
         style={{
           '--sidebar-object-fit': catalog.objectFitFor(associatedWorkspace) ?? catalog.objectFit,
           '--sidebar-width': sidebar ? `${sidebar?.width}px` : undefined
         } as any}
         data-valid={isValidForUpload}>
      {!detailDef ? <div>No detail definition exists for type {selectedPathItem?.libraryId ?? selectedPathItem?.idWithPath}</div>
                  :
       <Fragment>
         {type === 'sidebar' && <Fragment>
           {!isMaximized && <LeftSidebarHeader/>}

         <SidebarCarousel
           key={id}
				 className={css.carousel}
				 disableUpload={disableUpload}
				 localImages={localImages}
				 drag={drag}
				 data={data}
				 loading={isUploading}
				 align={align}
			 />
			 <div className={css.scrollContainer}>
               {sections.map((section, i) => section.type === 'secondaries' ? <CatalogSecondariesDetails key={i}/> : <SidebarSection align={align} key={i} section={section}/>)}
			 </div>
		 </Fragment>}
         {type == 'modal' && <CatalogItemModelContents className={css.modal}/>}
         {showImageFullscreen && <CatalogItemSidebarDetailsContext.Provider value={details}><Portal>
             <FullScreenDetailImages disableUpload={disableUpload}/>
         </Portal></CatalogItemSidebarDetailsContext.Provider>}
       </Fragment>
      }
      {/* <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      open={isSnackBarOpen}
      autoHideDuration={6000}
      message={snackBarMsg}
    /> */}
      <div className={css.mutationBar} data-visible={isDirty}>
        {saveButton}
        <SecondaryButton className={css.secondaryButton} disabled={isApplyingMutations} onClick={cancelMutations}>Cancel</SecondaryButton>
      </div>
    </div>
    {/* <Snackbar
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: align
    }}
    open={isSnackBarOpen || true}
    autoHideDuration={6000}
    message={snackBarMsg || 'test message'} 
  /> */}
  </Fragment>
})

export const SidebarSection = observer<{ align: SidebarAlign, section: DetailsViewSectionConfig }>(function SidebarSection({align, section: {fields, rows, itemSource, type, ...section}}) {
  const name = section.name ?? section.label,
    store = useLocalStore(() => new SideSectionCollapsableStore),
    {open, toggleExpanded} = store
  const content = <div className={css.sectionContent}>
    {(fields ?? []).map((f, j) => {
      return f?.rows && !f.fields ? <TableRegionView key={j} rows={f?.rows} hasFields={!!fields.length} itemSource={itemSource}/> : <CatalogFieldRenderer align={align} key={j} field={f} tabId={type}/>
    })}
    {!_.isEmpty(rows) && <TableRegionView rows={rows} hasFields={!!fields.length} itemSource={itemSource}/>}
  </div>

  return (
    <div className={css.section} data-expandable={name != null}>
      {name ?
       (<CollapsibleStateful className={css.collapsible} classes={{list: css.list, wrapperInner: css.listContent}} title={name}
                             open={open} onToggleOpen={() => toggleExpanded()}>
         {content}
       </CollapsibleStateful>)
            : content}
    </div>
  )
})

class SideSectionCollapsableStore {

  @observable open = true
  @action toggleExpanded = () => {
    this.open = !this.open
    return () => this.open
  }
}

export const FullScreenDetailImages = observer<{disableUpload?: boolean}>(function FullScreenDetailImages({disableUpload}) {
    const store = useCatalogItemSidebarDetailsStore(),
      {selectedImage, isUploading, manageImage, localImages, onCloseSlider, onChangeFileUpload, onDownloadImage, updateActiveImageId, selectedImageId, associatedWorkspaceForCurrentImage} = store

    return (<Fragment>

      <div className={css.sliderContainer}
           data-source={selectedImage?.source}
           data-workspace={associatedWorkspaceForCurrentImage?.id}
           style={associatedWorkspaceForCurrentImage?.catalog?.cssCardImage}
      >
        <Slider
          className={css.slider}
          localImages={localImages}
          onClose={onCloseSlider}
          onAddImage={onChangeFileUpload}
          onDownload={onDownloadImage}
          onDelete={manageImage}
          activeImageId={selectedImageId}
          updateActiveStepOnParent={updateActiveImageId}
          loading={isUploading}
          disableUpload={disableUpload}
          detailsStore={store}
        />
      </div>
    </Fragment>)
})


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/CatalogItemSidebarDetails.tsx